import { warning } from 'hey-listen';
import { secondsToMilliseconds } from '../utils/time-conversion.mjs';
import { instantAnimationState } from '../utils/use-instant-transition-state.mjs';
import { createAcceleratedAnimation } from './waapi/create-accelerated-animation.mjs';
import { createInstantAnimation } from './create-instant-animation.mjs';
import { animate } from './legacy-popmotion/index.mjs';
import { inertia } from './legacy-popmotion/inertia.mjs';
import { getDefaultTransition } from './utils/default-transitions.mjs';
import { isAnimatable } from './utils/is-animatable.mjs';
import { getKeyframes } from './utils/keyframes.mjs';
import { getValueTransition, isTransitionDefined } from './utils/transitions.mjs';
import { supports } from './waapi/supports.mjs';

/**
 * A list of values that can be hardware-accelerated.
 */
const acceleratedValues = new Set(["opacity"]);
const createMotionValueAnimation = function (valueName, value, target) {
  let transition = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return onComplete => {
    const valueTransition = getValueTransition(transition, valueName) || {};
    /**
     * Most transition values are currently completely overwritten by value-specific
     * transitions. In the future it'd be nicer to blend these transitions. But for now
     * delay actually does inherit from the root transition if not value-specific.
     */
    const delay = valueTransition.delay || transition.delay || 0;
    /**
     * Elapsed isn't a public transition option but can be passed through from
     * optimized appear effects in milliseconds.
     */
    let {
      elapsed = 0
    } = transition;
    elapsed = elapsed - secondsToMilliseconds(delay);
    const keyframes = getKeyframes(value, valueName, target, valueTransition);
    /**
     * Check if we're able to animate between the start and end keyframes,
     * and throw a warning if we're attempting to animate between one that's
     * animatable and another that isn't.
     */
    const originKeyframe = keyframes[0];
    const targetKeyframe = keyframes[keyframes.length - 1];
    const isOriginAnimatable = isAnimatable(valueName, originKeyframe);
    const isTargetAnimatable = isAnimatable(valueName, targetKeyframe);
    warning(isOriginAnimatable === isTargetAnimatable, "You are trying to animate ".concat(valueName, " from \"").concat(originKeyframe, "\" to \"").concat(targetKeyframe, "\". ").concat(originKeyframe, " is not an animatable value - to enable this animation set ").concat(originKeyframe, " to a value animatable to ").concat(targetKeyframe, " via the `style` property."));
    let options = {
      keyframes,
      velocity: value.getVelocity(),
      ...valueTransition,
      elapsed,
      onUpdate: v => {
        value.set(v);
        valueTransition.onUpdate && valueTransition.onUpdate(v);
      },
      onComplete: () => {
        onComplete();
        valueTransition.onComplete && valueTransition.onComplete();
      }
    };
    if (!isOriginAnimatable || !isTargetAnimatable || instantAnimationState.current || valueTransition.type === false) {
      /**
       * If we can't animate this value, or the global instant animation flag is set,
       * or this is simply defined as an instant transition, return an instant transition.
       */
      return createInstantAnimation(options);
    } else if (valueTransition.type === "inertia") {
      /**
       * If this is an inertia animation, we currently don't support pre-generating
       * keyframes for this as such it must always run on the main thread.
       */
      const animation = inertia(options);
      return () => animation.stop();
    }
    /**
     * If there's no transition defined for this value, we can generate
     * unqiue transition settings for this value.
     */
    if (!isTransitionDefined(valueTransition)) {
      options = {
        ...options,
        ...getDefaultTransition(valueName, options)
      };
    }
    /**
     * Both WAAPI and our internal animation functions use durations
     * as defined by milliseconds, while our external API defines them
     * as seconds.
     */
    if (options.duration) {
      options.duration = secondsToMilliseconds(options.duration);
    }
    if (options.repeatDelay) {
      options.repeatDelay = secondsToMilliseconds(options.repeatDelay);
    }
    const visualElement = value.owner;
    const element = visualElement && visualElement.current;
    const canAccelerateAnimation = supports.waapi() && acceleratedValues.has(valueName) && !options.repeatDelay && options.repeatType !== "mirror" && options.damping !== 0 && visualElement && element instanceof HTMLElement && !visualElement.getProps().onUpdate;
    if (canAccelerateAnimation) {
      /**
       * If this animation is capable of being run via WAAPI, then do so.
       */
      return createAcceleratedAnimation(value, valueName, options);
    } else {
      /**
       * Otherwise, fall back to the main thread.
       */
      const animation = animate(options);
      return () => animation.stop();
    }
  };
};
export { createMotionValueAnimation };